import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import api from "../api/api";
import { ProductServices } from "../services/ProductsServices";
import ProductCard from "./ProductCard";

// ✅ Reusable Arrows (identical to NewArrivalOne)
const SampleNextArrow = memo(({ className, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
    aria-label="Next"
  >
    <i className="ph ph-caret-right" />
  </button>
));

const SamplePrevArrow = memo(({ className, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
    aria-label="Previous"
  >
    <i className="ph ph-caret-left" />
  </button>
));

/* -------------------------
   Image URL helper (same logic as NewArrivalOne)
-------------------------- */
const getProductImageUrl = (product) => {
  if (!product?.image) return "assets/images/thumbs/NewArrivals/product-img21.png";

  const img = String(product.image).trim();
  if (img.startsWith("http://") || img.startsWith("https://")) return img;

  const backendRoot = (api.defaults.baseURL || "").replace(/\/$/, "").replace(/\/api$/, "");
  if (img.startsWith("storage/")) return `${backendRoot}/${img}`;
  if (img.startsWith("/storage/")) return `${backendRoot}${img}`;
  return `${backendRoot}/storage/${img}`;
};

/* -------------------------
   Discount helpers (same as NewArrivalOne)
-------------------------- */
const getDiscountPercent = (regular, current) => {
  const reg = Number(regular);
  const cur = Number(current);
  if (!isFinite(reg) || !isFinite(cur) || reg <= 0 || cur <= 0 || cur >= reg) return null;
  const percent = Math.round(((reg - cur) / reg) * 100);
  return percent > 0 ? percent : null;
};
const discountLabel = (percent) => (percent ? `${percent}% OFF` : null);

/* =========================================================
   NewArrivalTwo — "You Might Also Like" (same-category)
========================================================= */
const NewArrivalTwo = ({ currentProductId }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: true,
      infinite: products.length > 6,
      speed: 800,
      slidesToShow: 6,
      slidesToScroll: 1,
      autoplay: true,
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
      responsive: [
        { breakpoint: 1599, settings: { slidesToShow: 6 } },
        { breakpoint: 1399, settings: { slidesToShow: 4 } },
        { breakpoint: 992, settings: { slidesToShow: 3 } },
        { breakpoint: 575, settings: { slidesToShow: 2 } },
        { breakpoint: 424, settings: { slidesToShow: 1 } },
      ],
    }),
    [products.length]
  );

  useEffect(() => {
    if (!currentProductId) return;

    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);

        // 1️⃣ Get current product → extract category info
        const current = await ProductServices.getProductById(currentProductId);
        if (!mounted || !current) return;

        const categoryId   = current.category_id   ?? null;
        const categoryName = current.category_name ?? null;

        // 2️⃣ Fetch same-category products
        //    The component tries three strategies — keep whichever matches your API:
        //
        //    Option A (preferred) — dedicated endpoint by category ID:
        //      ProductServices.getProductsByCategoryId(categoryId)
        //
        //    Option B — dedicated endpoint by category name:
        //      ProductServices.getProductsByCategory(categoryName)
        //
        //    Option C (fallback) — fetch all, filter client-side:
        //      ProductServices.getAllProducts()
        //
        let fetched = [];

        if (categoryId != null && ProductServices.getProductsByCategoryId) {
          fetched = await ProductServices.getProductsByCategoryId(categoryId);
        } else if (categoryName && ProductServices.getProductsByCategory) {
          fetched = await ProductServices.getProductsByCategory(categoryName);
        } else if (ProductServices.getAllProducts) {
          const all = await ProductServices.getAllProducts();
          fetched = (Array.isArray(all) ? all : []).filter((p) => {
            if (categoryId   != null) return String(p?.category_id)   === String(categoryId);
            if (categoryName)         return p?.category_name         === categoryName;
            return false;
          });
        }

        if (!mounted) return;

        // 3️⃣ Exclude the current product
        const filtered = (Array.isArray(fetched) ? fetched : []).filter(
          (p) => String(p?.id) !== String(currentProductId)
        );

        setProducts(filtered);
      } catch (err) {
        console.error("NewArrivalTwo fetch error:", err);
        if (mounted) setProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [currentProductId]);

  // Map API shape → ProductCard props (identical pattern to NewArrivalOne)
  const sliderCards = useMemo(() => {
    return products.map((p) => {
      const percent = getDiscountPercent(p.base_regular_price, p.base_current_price);

      return {
        id:       p.id,
        imageSrc: getProductImageUrl(p),
        title:    p.title,
        category: p.category_name || "Uncategorized",
        oldPrice: p.base_regular_price ? `$${Number(p.base_regular_price).toFixed(2)}` : "$0.00",
        newPrice: p.base_current_price ? `$${Number(p.base_current_price).toFixed(2)}` : "$0.00",
        discount: discountLabel(percent),
        inStock:  true,
      };
    });
  }, [products]);

  // Nothing to show → render nothing
  if (!loading && sliderCards.length === 0) return null;

  return (
    <section className="new-arrival pb-80">
      <div className="container container-lg">
        <div className="section-heading">
          <div className="flex-between flex-wrap gap-8">
            <h5 className="mb-0">You Might Also Like</h5>
            <div className="flex-align mr-point gap-16">
              <Link
                to="/shop"
                className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
              >
                All Products
              </Link>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <p className="text-gray-600 mb-0">Loading similar products...</p>
          </div>
        ) : (
          <div className="new-arrival__slider arrow-style-two">
            <Slider {...settings}>
              {sliderCards.map((card) => (
                <div key={card.id} className="px-8">
                  {/* ✅ noGrid=true so it doesn't apply bootstrap columns */}
                  <ProductCard {...card} noGrid />
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewArrivalTwo;